import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';
import { VideoMosaicBackground } from '@/components/VideoMosaicBackground';
import { TwinneeWordmark } from '@/components/TwinneeWordmark';

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
      toast.success(data?.message || 'Check your email for reset instructions.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-[#0a0a0f]">
      <VideoMosaicBackground variant="auth" showTitles={false} />

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 25 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-8">
          <Link to="/" aria-label="Twinnee home" className="inline-flex items-center mb-6">
            <TwinneeWordmark className="h-11" />
          </Link>
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight text-white">
            Forgot password?
          </h1>
          <p className="text-sm text-slate-400 mt-2">
            {sent
              ? 'If an account exists for that email, we sent reset instructions.'
              : 'Enter your email and we will send you a reset link.'}
          </p>
        </div>

        <div className="rounded-3xl p-8 shadow-xl border border-white/10 bg-black/55 backdrop-blur-xl">
          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-sm font-medium text-slate-200">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="username"
                    className="auth-form-input h-12 pl-10 rounded-xl border-2 border-white/15 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/20"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-accent text-base shadow-lg shadow-amber-500/25 hover:scale-[1.02] active:scale-95 transition-all border-0"
              >
                {loading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                  />
                ) : (
                  <>
                    Send reset link
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div className="space-y-4 text-center text-sm text-slate-300">
              <p>Check your inbox and spam folder. The link expires in one hour.</p>
              <Button
                type="button"
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10"
                onClick={() => setSent(false)}
              >
                Send again
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm text-amber-400 font-medium hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to sign in
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
